import { useMemo } from "react";
import { Flex, Text, TextField, Switch, Badge } from "@radix-ui/themes";
import { toast } from "sonner";

import { useTopicDraft } from "@/v2/features/simulator/context/useTopicDraft";
import { useSimulator } from "../../context/SimulatorWrapper";
import styles from "../instructions/styles.module.scss";

export const TopicOverrideConfig = ({ topicId }: { topicId: number }) => {
  const { updateTopicReleasePolicy, currentRelease, simulator } =
    useSimulator();
  const { overrideStrictMode, overrideMaxBackAndForth } = useTopicDraft({
    topicId,
  });

  const releaseId = useMemo(() => currentRelease?.id, [currentRelease]);

  const isChat = useMemo(
    () => simulator?.channel === "autonomous_quackchat",
    [simulator?.channel]
  );

  const releaseConfigs = useMemo(() => {
    return {
      strictMode: currentRelease?.strictMode ?? false,
      maxBackAndForth: currentRelease?.maxBackAndForth ?? 0,
    };
  }, [currentRelease]);

  const handleStrictModeChange = (value: boolean) => {
    if (!releaseId) {
      toast.error("Release is not found");
      return;
    }

    const data = {
      topicId,
      releaseId,
      overrideStrictMode: value,
    };
    updateTopicReleasePolicy(topicId, releaseId, data);
  };

  const handleMaxBackAndForthChange = (value: number) => {
    if (!releaseId) {
      toast.error("Release is not found");
      return;
    }

    const data = {
      topicId,
      releaseId,
      overrideMaxBackAndForth: value || undefined,
    };
    updateTopicReleasePolicy(topicId, releaseId, data);
  };

  const isStrictModeOverridden = useMemo(
    () => overrideStrictMode !== undefined && overrideStrictMode !== null,
    [overrideStrictMode]
  );
  const isMaxBackAndForthOverridden = useMemo(
    () =>
      overrideMaxBackAndForth !== undefined && overrideMaxBackAndForth !== null,
    [overrideMaxBackAndForth]
  );

  return (
    <div className={styles.overrideConfigSection}>
      <Flex direction="column" gap="1">
        <Flex align="center" gap="2">
          <Text size="2" weight="medium">
            Override Configs
          </Text>
        </Flex>

        <Flex direction="column" gap="1">
          <Flex align="center" gap="3">
            <Text size="1">Strict Mode</Text>
            <Switch
              size="1"
              checked={overrideStrictMode ?? releaseConfigs.strictMode}
              onCheckedChange={handleStrictModeChange}
            />
            {isStrictModeOverridden &&
            overrideStrictMode !== releaseConfigs.strictMode ? (
              <Badge color="blue">Overridden</Badge>
            ) : (
              <Badge color="gray">Default</Badge>
            )}
          </Flex>

          {!isChat && (
            <Flex align="center" gap="3">
              <Text size="1">Max Back & Forth</Text>
              <TextField.Root
                type="number"
                size="1"
                value={overrideMaxBackAndForth ?? releaseConfigs.maxBackAndForth}
                onChange={(e) =>
                  handleMaxBackAndForthChange(parseInt(e.target.value) || 0)
                }
                style={{ width: "60px" }}
              />
              {isMaxBackAndForthOverridden &&
              overrideMaxBackAndForth !== releaseConfigs.maxBackAndForth ? (
                <Badge color="blue">Overridden</Badge>
              ) : (
                <Badge color="gray">Default</Badge>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
};
